import { produce } from 'immer'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { To, useNavigate } from 'react-router-dom'
import { SWRResponse } from 'swr'

import {
    Alert, Button, ButtonGroup, Divider, H4, H6, Icon, Intent, Section, SectionCard
} from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'

import useAuth from '../../aws/useAuth'
import useCurrentUserUUID from '../../aws/useCurrentUserUUID'
import card from '../../domain/api/card'
import me from '../../domain/api/me'
import profile from '../../domain/api/profile'
import { GetCardResponseEntity } from '../../domain/entities/card'
import { GetProfileResponseEntity } from '../../domain/entities/profile'
import ApiError from '../common/apiError/ApiError'
import Empty from '../common/empty/Empty'
import Mount from '../common/mount/Mount'
import ProfileDetail from '../profileDetail/ProfileDetail'
import { useRootOutletContext } from '../root/Root'
import styles from './CardDetail.module.scss'
import CardDetailSelectProfileToLinkSkeleton from './CardDetailSelectProfileToLinkSkeleton'
import CardDetailSkeleton from './CardDetailSkeleton'

export default ({
    signInLink,
    cardResponse: { data, mutate: setData }
}: {
    signInLink: (cardId: string) => To,
    cardResponse: SWRResponse<GetCardResponseEntity>,
}) => {
    const { t } = useTranslation()
    const { toaster } = useRootOutletContext()
    const navigate = useNavigate()
    const linkedProfileResponse = profile.useFetchSWR(data?.profile_id)
    const { mutate: mutateLinkedProfile } = linkedProfileResponse
    const myProfilesResponse = me.profiles.useFetchSWR()
    const { mutate: mutateMyProfiles } = myProfilesResponse
    const { data: myCards, mutate: mutateMyCards } = me.cards.useFetchSWR()
    const { trigger: insertProfile, isMutating: isInsertingProfile } = profile.useInsertSWR()
    const { trigger: modifyCard, isMutating: isModifyingCard } = card.useModifySWR(data?.id)
    const auth = useAuth()
    const cognitoUUID = useCurrentUserUUID()
    const isMyCard = !!cognitoUUID && data?.cognito_uuid === cognitoUUID

    const [state, setState] = useState({
        isProfilesOpen: false,
        isUnlinkAlertOpen: false,
    })

    const unlinkConfirmed = useCallback(async () => {
        const result = await modifyCard({ profile_id: undefined })
        setState(produce(draft => {
            draft.isUnlinkAlertOpen = false
        }))

        if (result.status !== 200)
            return toaster?.show({ message: t('toast.failedToUnlinkProfile.message'), icon: IconNames.Cross, intent: Intent.DANGER })

        mutateMyCards(prev => produce(prev, draft => {
            const card = draft?.find(card => card.id === data?.id)
            if (card) card.profile_id = undefined
        }), { revalidate: false })

        setData(produce(draft => {
            draft.profile_id = undefined
        }))
        mutateLinkedProfile(undefined, { revalidate: false })
        toaster?.show({ message: t('toast.profileUnlinked.message'), icon: IconNames.Unlink, intent: Intent.SUCCESS })
    }, [data?.id, mutateMyCards, modifyCard, toaster, t, setData, setState, mutateLinkedProfile])

    const selectProfile = useCallback((profile: GetProfileResponseEntity) => async (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation()

        const result = await modifyCard({ profile_id: profile.id })
        if (result.status !== 200)
            return toaster?.show({ message: t('toast.failedToLinkProfile.message'), icon: IconNames.Cross, intent: Intent.DANGER })

        mutateMyCards(prev => produce(prev, draft => {
            const card = draft?.find(card => card.id === data?.id)
            if (card) card.profile_id = profile.id
        }), { revalidate: false })
        setData(produce(draft => {
            draft.profile_id = profile.id
        }))
        mutateLinkedProfile(profile, { revalidate: false })
        setState(produce(draft => {
            draft.isProfilesOpen = false
        }))
        toaster?.show({ message: t('toast.profileLinked.message'), icon: IconNames.Link, intent: Intent.SUCCESS })
    }, [data?.id, mutateMyCards, modifyCard, toaster, t, setData, setState, mutateLinkedProfile])

    const addProfile = useCallback(async (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation()

        const result = await insertProfile()
        if (result.status !== 201)
            return toaster?.show({ message: t('toast.failedToAddNewProfile.message'), icon: IconNames.Cross, intent: Intent.DANGER })
        mutateMyProfiles(produce(draft => { draft?.push(result.data) }), { revalidate: false })
        toaster?.show({ message: t('toast.newProfileAdded.message'), icon: IconNames.Add, intent: Intent.SUCCESS })

        if ((await modifyCard({ profile_id: result.data.id })).status !== 200) return

        setData(produce(draft => {
            draft.profile_id = result.data.id
        }))
        mutateLinkedProfile(result.data, { revalidate: false })
        setState(produce(draft => {
            draft.isProfilesOpen = false
        }))
    }, [insertProfile, mutateMyProfiles, toaster, t, setData, setState, modifyCard, mutateLinkedProfile])

    const linkState = useCallback((profileId: string) =>
        profileId === data?.profile_id
            ? 'linked'
            : myCards?.some(card => card.profile_id === profileId)
                ? 'linkedToOtherCard'
                : 'unlinked',
        [myCards, data?.profile_id])

    return <>
        <Alert
            className={styles.alert}
            icon={IconNames.Unlink}
            intent={Intent.DANGER}
            isOpen={state.isUnlinkAlertOpen}
            loading={isModifyingCard}
            canEscapeKeyCancel
            canOutsideClickCancel
            cancelButtonText={t('cardDetail.unlinkProfileAlert.cancelButton.label')}
            confirmButtonText={t('cardDetail.unlinkProfileAlert.confirmButton.label')}
            onCancel={() => setState(produce(draft => {
                draft.isUnlinkAlertOpen = false
            }))}
            onConfirm={unlinkConfirmed}
        >
            <p>{t('cardDetail.unlinkProfileAlert.message')}</p>
        </Alert>

        <Alert
            className={`${styles.alert} ${styles.selectProfileToLink}`}
            isOpen={state.isProfilesOpen}
            canEscapeKeyCancel
            canOutsideClickCancel
            confirmButtonText={t('cardDetail.selectProfileToLink.cancelButton.label')}
            onConfirm={() => setState(produce(draft => {
                draft.isProfilesOpen = false
            }))}
            onClose={() => setState(produce(draft => {
                draft.isProfilesOpen = false
            }))}>
            <H4><Icon className={styles.icon} icon={IconNames.Link} />{t('cardDetail.selectProfileToLink.title')}</H4>
            <Divider className={styles.divider} />
            <H6>{t('cardDetail.selectProfileToLink.subtitle')}</H6>
            <Divider className={styles.spacer} />

            <div className={styles.verticalContainer}>
                <Mount response={myProfilesResponse}
                    loading={() => <CardDetailSelectProfileToLinkSkeleton />}
                    failure={error => <ApiError error={error} />}
                    empty={() =>
                        <Empty
                            lottie='profile'
                            title={t('cardDetail.selectProfileToLink.empty.label')}
                            description={t('cardDetail.selectProfileToLink.empty.description')}
                            buttonProps={{
                                text: t('cardDetail.selectProfileToLink.empty.addNewButton.label'),
                                icon: IconNames.Add,
                                onClick: addProfile,
                                disabled: isInsertingProfile || isModifyingCard,
                                fill: true,
                            }} />
                    }
                    success={profiles => <>
                        {[...profiles] // sort mutates the original array and can cause an error
                            .sort((a, b) => a.created_at - b.created_at)
                            .map((profile, index) =>
                                <Section
                                    className={styles.profileSection}
                                    key={`section-${index}`}
                                    title={`#${(index + 1).toString().padStart(3, '0')}`}
                                    subtitle={linkState(profile.id) === 'linked'
                                        ? t('cardDetail.selectProfileToLink.alreadyLinked.subtitle')
                                        : linkState(profile.id) === 'linkedToOtherCard'
                                            ? t('cardDetail.selectProfileToLink.alreadyLinkedToOtherCard.subtitle')
                                            : t('cardDetail.selectProfileToLink.lastUpdated.label', {
                                                date: new Date(profile.updated_at * 1000),
                                            })}
                                    rightElement={<Button
                                        text={t('cardDetail.selectProfileToLink.selectButton.label')}
                                        onClick={selectProfile(profile)}
                                        intent={linkState(profile.id) === 'unlinked' ? Intent.PRIMARY : Intent.NONE}
                                        disabled={linkState(profile.id) !== 'unlinked' || isModifyingCard}
                                        minimal
                                        small />}
                                    collapsible
                                    collapseProps={{ defaultIsOpen: false }}>
                                    <SectionCard>
                                        <ProfileDetail data={profile} />
                                    </SectionCard>
                                </Section>)}
                    </>}
                />
            </div>
        </Alert>

        <H4>{t('cardDetail.title')}</H4>

        <Mount response={linkedProfileResponse}
            loading={() => <CardDetailSkeleton />}
            failure={error => <ApiError error={error} />}
            success={data => <ProfileDetail data={data} canEdit />}
            idling={() => <>
                {!auth.isAuthenticated && <>
                    <Empty
                        lottie="link"
                        title={t('cardDetail.signInThenlinkProfile.label')}
                        description={t('cardDetail.signInThenlinkProfile.description')}
                        buttonProps={{
                            icon: IconNames.LogIn,
                            text: t('cardDetail.signInThenlinkProfile.signInButton.label'),
                            onClick: () => navigate(signInLink(data?.id ?? '')), // logically data should never be undefined here
                        }}
                    />
                </>}
            </>}
        />

        {isMyCard && <>
            {data?.profile_id ? <>
                <ButtonGroup fill>
                    <Button
                        className={styles.profileButton}
                        text={t('cardDetail.changeProfileButton.label')}
                        intent={Intent.WARNING}
                        onClick={() => setState(produce(draft => {
                            draft.isProfilesOpen = true
                        }))} />
                    <Button
                        className={styles.profileButton}
                        text={t('cardDetail.unlinkProfileButton.label')}
                        intent={Intent.DANGER}
                        onClick={() => setState(produce(draft => {
                            draft.isUnlinkAlertOpen = true
                        }))} />
                </ButtonGroup>
            </> :
                <Empty
                    lottie="link"
                    title={t('cardDetail.linkProfile.label')}
                    description={t('cardDetail.linkProfile.description')}
                    buttonProps={{
                        text: t('cardDetail.linkProfile.linkButton.label'),
                        icon: IconNames.Link,
                        onClick: () => setState(produce(draft => {
                            draft.isProfilesOpen = true
                        })),
                    }}
                />}
            <Divider className={styles.spacer} />
        </>}
    </>
}